import React, { useState, useMemo, useEffect } from "react";
import {
  BarChart,
  Bar,
  Line,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ComposedChart,
} from "recharts";
import Select from "react-select";
import { formatValue } from "../../utils/formatNumber";
import { getChartColors, getThemeColors, Theme } from "../../theme/colors";

type DataItem = Record<string, string | number>;

interface GenericChartMobileProps {
  data: DataItem[];
  theme: Theme;
  username?: string;
}

// Categorize keys from data into dimensions and measures.
const categorizeKeys = (data: DataItem[]): { dimensions: string[]; measures: string[] } => {
  if (data.length === 0) return { dimensions: [], measures: [] };
  const firstItem = data[0];
  const dimensions: string[] = [];
  const measures: string[] = [];
  Object.keys(firstItem).forEach((key) => {
    if (typeof firstItem[key] === "number") {
      if (
        (key.toLowerCase().includes("year") &&
          firstItem[key] > 2000 &&
          firstItem[key] < 2030) ||
        (key.includes("month") &&
          firstItem[key] >= 1 &&
          firstItem[key] <= 12) ||
        (key.includes("week") && firstItem[key] >= 1 && firstItem[key] <= 52)
      ) {
        dimensions.push(key);
      } else {
        measures.push(key);
      }
    } else {
      dimensions.push(key);
    }
  });
  return { dimensions, measures };
};

const GenericChartMobile: React.FC<GenericChartMobileProps> = ({ theme, data, username = 'default' }) => {
  // Use our helper to get dimensions and measures.
  const { dimensions, measures } = useMemo(() => categorizeKeys(data), [data]);

  // Multi-select dimensions for the x-axis.
  const [xAxisKeys, setXAxisKeys] = useState<string[]>(dimensions.length > 0 ? [dimensions[0]] : []);
  useEffect(() => {
    const valid = dimensions.filter((dim) => xAxisKeys.includes(dim));
    if (valid.length === 0 && dimensions.length > 0) {
      setXAxisKeys([dimensions[0]]);
    } else if (valid.length !== xAxisKeys.length) {
      setXAxisKeys(valid);
    }
  }, [dimensions, xAxisKeys]);

  // Chart type and measure selections.
  const [chartType, setChartType] = useState<"bar" | "pie" | "combined">("bar");
  const [selectedKeys, setSelectedKeys] = useState<string[]>(measures.length > 0 ? [measures[0]] : []);
  const [barKeys, setBarKeys] = useState<string[]>(measures.length > 1 ? [measures[1]] : []);

  // Sorting & Pagination states.
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [sortKey, setSortKey] = useState<string>("");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("asc");
  const [pageSize, setPageSize] = useState<number | "all">(5);

  // Sorting logic: sort data if a sort field is chosen.
  const sortedData = useMemo(() => {
    if (!sortKey) return data;
    return [...data].sort((a, b) => {
      const aVal = a[sortKey];
      const bVal = b[sortKey];
      if (typeof aVal === "number" && typeof bVal === "number") {
        return sortOrder === "asc" ? aVal - bVal : bVal - aVal;
      }
      return sortOrder === "asc"
        ? String(aVal).localeCompare(String(bVal))
        : String(bVal).localeCompare(String(aVal));
    });
  }, [data, sortKey, sortOrder]);

  // Pagination: slice the sorted data.
  const paginatedData = useMemo(() => {
    if (pageSize === "all") return sortedData;
    const startIndex = (currentPage - 1) * (pageSize as number);
    return sortedData.slice(startIndex, startIndex + (pageSize as number));
  }, [sortedData, currentPage, pageSize]);

  // Create a composite field by concatenating the selected dimensions.
  const compositeData = useMemo(() => {
    if (xAxisKeys.length === 0) return paginatedData;
    return paginatedData.map((item) => ({
      ...item,
      compositeDimension: xAxisKeys.map((key) => item[key]).join(" - "),
    }));
  }, [paginatedData, xAxisKeys]);

  // Dropdown options.
  const dimensionOptions = dimensions.map((key) => ({ value: key, label: key }));
  const measureOptions = measures.map((key) => ({ value: key, label: key }));
  const barKeyOptions = measures.map((key) => ({ value: key, label: key }));
  const sortOptions = [...dimensionOptions, ...measureOptions];
  const pageSizeOptions = [
    { value: 5, label: "5" },
    { value: 10, label: "10" },
    { value: 15, label: "15" },
    { value: 20, label: "20" },
    { value: "all", label: "All" },
  ];
  const typeOptions = [
    { value: "bar", label: "Bar Chart" },
    { value: "pie", label: "Pie Chart" },
    { value: "combined", label: "Combined Bar & Line Chart" },
  ];

  const colorTheme = getThemeColors(username, theme);
  const CHART_COLORS_MOBILE = getChartColors(username, theme).mobile;

  const customStyles = {
    control: (provided: any) => ({
      ...provided,
      backgroundColor: colorTheme.background,
      color: colorTheme.text,
      borderColor: colorTheme.border,
    }),
    singleValue: (provided: any) => ({
      ...provided,
      backgroundColor: colorTheme.background,
      color: colorTheme.text,
    }),
    option: (provided: any, state: { isSelected: any }) => ({
      ...provided,
      backgroundColor: state.isSelected
        ? colorTheme.multiValueBg
        : colorTheme.background,
      color: colorTheme.text,
      ":hover": {
        backgroundColor: colorTheme.optionHoverBg,
      },
    }),
    menu: (provided: any) => ({
      ...provided,
      backgroundColor: colorTheme.background,
    }),
    multiValue: (provided: any) => ({
      ...provided,
      backgroundColor: colorTheme.multiValueBg,
    }),
    multiValueLabel: (provided: any) => ({
      ...provided,
      color: colorTheme.text,
    }),
    multiValueRemove: (provided: any) => ({
      ...provided,
      color: colorTheme.text,
      ":hover": {
        backgroundColor: colorTheme.removeValueBg,
        color: colorTheme.text,
      },
    }),
  };

  // Chart renderers – they use compositeData and the compositeDimension for the x‑axis.
  const renderBarChart = () => (
    <ResponsiveContainer width="100%" height={300}>
      <BarChart data={compositeData}>
        <XAxis dataKey="compositeDimension" tickFormatter={(value: string | number) => formatValue(value, true)} />
        <YAxis />
        <Tooltip
          contentStyle={{
            backgroundColor: colorTheme.background,
            borderColor: colorTheme.border,
            color: colorTheme.text,
            opacity: 0.9,
          }}
          cursor={false}
          formatter={(value: string | number) => formatValue(value)}
        />
        <Legend />
        {selectedKeys
          .filter((key) => !xAxisKeys.includes(key))
          .map((key, index) => (
            <Bar key={key} dataKey={key} fill={CHART_COLORS_MOBILE[index % CHART_COLORS_MOBILE.length]} />
          ))}
      </BarChart>
    </ResponsiveContainer>
  );

  const renderPieChart = () => (
    <ResponsiveContainer width="100%" height={300}>
      <PieChart>
        {selectedKeys.map((key, index) => (
          <Pie
            key={key}
            data={compositeData}
            dataKey={key}
            nameKey="compositeDimension"
            cx="50%"
            cy="50%"
            outerRadius={50 + index * 20}
            fill={CHART_COLORS_MOBILE[index % CHART_COLORS_MOBILE.length]}
            label
          >
            {compositeData.map((entry, idx) => (
              <Cell key={`cell-${idx}`} fill={CHART_COLORS_MOBILE[idx % CHART_COLORS_MOBILE.length]} />
            ))}
          </Pie>
        ))}
        <Tooltip formatter={(value: string | number) => formatValue(value)} />
        <Legend />
      </PieChart>
    </ResponsiveContainer>
  );

  const renderCombinedChart = () => (
    <ResponsiveContainer width="100%" height={300}>
      <ComposedChart data={compositeData}>
        <XAxis dataKey="compositeDimension" tickFormatter={(value: string | number) => formatValue(value, true)} />
        <YAxis yAxisId="left" />
        <YAxis yAxisId="right" orientation="right" />
        <Tooltip
          contentStyle={{
            backgroundColor: colorTheme.background,
            borderColor: colorTheme.border,
            color: colorTheme.text,
            opacity: 0.9,
          }}
          cursor={false}
          formatter={(value: string | number) => formatValue(value)}
        />
        <Legend />
        {barKeys.map((key, index) => (
          <Bar yAxisId="left" key={key} dataKey={key} fill={CHART_COLORS_MOBILE[index % CHART_COLORS_MOBILE.length]} />
        ))}
        {selectedKeys
          .filter((key) => !xAxisKeys.includes(key) && !barKeys.includes(key))
          .map((key, index) => (
            <Line
              key={key}
              yAxisId="right"
              type="monotone"
              dataKey={key}
              stroke={CHART_COLORS_MOBILE[(index + barKeys.length) % CHART_COLORS_MOBILE.length]}
            />
          ))}
      </ComposedChart>
    </ResponsiveContainer>
  );

  const renderChart = () => {
    switch (chartType) {
      case "bar":
        return renderBarChart();
      case "pie":
        return renderPieChart();
      case "combined":
        return renderCombinedChart();
      default:
        return null;
    }
  };

  return (
    <div style={{ padding: "10px", fontSize: "14px" }}>
      {/* Type Selector */}
      <div style={{ display: "flex", flexDirection: "column", gap: "5px" }}>
        <span>Type:</span>
        <Select
          options={typeOptions}
          value={typeOptions.find((option) => option.value === chartType)}
          onChange={(selectedOption) => {
            if (selectedOption) {
              setChartType(selectedOption.value as "bar" | "pie" | "combined");
            }
          }}
          styles={customStyles}
        />
      </div>

      {/* Dimension & Measure Selectors */}
      <div style={{ display: "flex", flexDirection: "column", gap: "10px", marginTop: "10px" }}>
        <div style={{ display: "flex", flexDirection: "column", gap: "5px" }}>
          <span>Dimensions:</span>
          <Select
            isMulti
            options={dimensionOptions}
            value={dimensionOptions.filter((option) => xAxisKeys.includes(option.value))}
            onChange={(selectedOptions) => {
              const values = selectedOptions.map((option) => option.value);
              setXAxisKeys(values);
            }}
            styles={customStyles}
            placeholder="Select dimensions..."
          />
        </div>
        <div style={{ display: "flex", flexDirection: "column" }}>
          {chartType === "bar" && (
            <div style={{ display: "flex", flexDirection: "column", gap: "5px" }}>
              <span>Measures:</span>
              <Select
                isMulti
                options={measureOptions}
                value={measureOptions.filter((option) => selectedKeys.includes(option.value))}
                onChange={(selectedOptions) => {
                  const values = selectedOptions.map((option) => option.value);
                  setSelectedKeys(values);
                }}
                closeMenuOnSelect={false}
                hideSelectedOptions={false}
                styles={customStyles}
                placeholder="Select measures..."
              />
            </div>
          )}
          {chartType === "combined" && (
            <div style={{ display: "flex", flexDirection: "column", gap: "5px" }}>
              <div style={{ display: "flex", flexDirection: "column", gap: "5px" }}>
                <span>Measures (Left axis):</span>
                <Select
                  isMulti
                  options={barKeyOptions}
                  value={barKeyOptions.filter((option) => barKeys.includes(option.value))}
                  onChange={(selectedOptions) => {
                    const values = selectedOptions.map((option) => option.value);
                    setBarKeys(values);
                  }}
                  closeMenuOnSelect={false}
                  hideSelectedOptions={false}
                  styles={customStyles}
                  placeholder="Select bar keys..."
                />
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: "5px" }}>
                <span>Measures (Right axis):</span>
                <Select
                  isMulti
                  options={measureOptions}
                  value={measureOptions.filter((option) => selectedKeys.includes(option.value))}
                  onChange={(selectedOptions) => {
                    const values = selectedOptions.map((option) => option.value);
                    setSelectedKeys(values);
                  }}
                  closeMenuOnSelect={false}
                  hideSelectedOptions={false}
                  styles={customStyles}
                  placeholder="Select measures..."
                />
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Sorting & Pagination Controls */}
      <div style={{ display: "flex", flexDirection: "column", gap: "5px", marginTop: "10px" }}>
        <div style={{ display: "flex", flexDirection: "column", gap: "5px" }}>
          <span>Sort By:</span>
          <Select
            options={sortOptions}
            value={sortKey ? sortOptions.find((option) => option.value === sortKey) : null}
            onChange={(selectedOption) => {
              if (selectedOption) {
                setSortKey(selectedOption.value);
                setCurrentPage(1);
              }
            }}
            styles={customStyles}
            placeholder="Select field to sort by..."
          />
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: "5px" }}>
          <span>Sort Order:</span>
          <Select
            options={[
              { value: "asc", label: "Ascending" },
              { value: "desc", label: "Descending" },
            ]}
            value={{
              value: sortOrder,
              label: sortOrder === "asc" ? "Ascending" : "Descending",
            }}
            onChange={(selectedOption) => {
              if (selectedOption) {
                setSortOrder(selectedOption.value);
                setCurrentPage(1);
              }
            }}
            styles={customStyles}
          />
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: "5px" }}>
          <span>Items per page:</span>
          <Select
            options={pageSizeOptions}
            value={pageSizeOptions.find((option) => option.value === pageSize) || null}
            onChange={(selectedOption) => {
              if (selectedOption) {
                setPageSize(selectedOption.value as number | "all");
                setCurrentPage(1);
              }
            }}
            styles={customStyles}
          />
        </div>
      </div>

      {/* Render the Chart */}
      <div style={{ marginTop: "20px" }}>{renderChart()}</div>

      {/* Pagination Buttons */}
      {pageSize !== "all" && (
        <div
          style={{
            marginTop: "20px",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <button
            onClick={() => setCurrentPage((prev) => (prev > 1 ? prev - 1 : prev))}
            disabled={currentPage === 1}
            style={{ padding: "5px 10px" }}
          >
            Previous
          </button>
          <span>
            Page {currentPage} of {Math.ceil(sortedData.length / (pageSize as number)) || 1}
          </span>
          <button
            onClick={() =>
              setCurrentPage((prev) =>
                prev < Math.ceil(sortedData.length / (pageSize as number)) ? prev + 1 : prev
              )
            }
            disabled={currentPage === Math.ceil(sortedData.length / (pageSize as number))}
            style={{ padding: "5px 10px" }}
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
};

export default GenericChartMobile;
